import { getRequestConfig } from 'api';
import {
  AccessLogItem,
  ActivityItem,
  ActivityPeriod,
  AdminForm,
  AdminGroup,
  AdminGroupDraft,
  AdminLocationHierarchy,
  AdminUser,
  AdminUserDraft,
  Campaign,
  CampaignDraft,
  FormList,
  GridMode,
  InviteTemplate,
  LocationNode,
  UserStats,
} from 'Interfaces/Admin';

/**
 *  Every endpoint of AdminController, in one place. These replace the Backbone
 *  sync functions (WebContent-src/admin/sync) and the inline $.ajax calls that
 *  used to live in the views.
 *
 *  Each returns the parsed JSON and lets the caller decide how to report
 *  failures, so that toasts and alerts stay in the components.
 */

const json = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const config = getRequestConfig();
  const res = await fetch(url, { ...config, ...init });
  return res.json() as Promise<T>;
};

const post = <T>(url: string, body?: unknown): Promise<T> =>
  json<T>(url, body === undefined ? { method: 'POST' } : { method: 'POST', body: JSON.stringify(body) });

const get = <T>(url: string): Promise<T> => json<T>(url);

/** Envelope returned by the save/delete endpoints (admin.util.RequestWrapper). */
export interface AdminResponse {
  status: 'OK' | 'FAIL' | 'PARTIAL';
  message?: string;
  errorMessage?: string;
  errorCode?: string;
  ids?: number[];
  group?: AdminGroup;
  user?: AdminUser;
  campaign?: Campaign;
}

export interface UserGroupsResponse {
  groupList: AdminGroup[];
  userList: AdminUser[];
  formLists: FormList[];
  forms: AdminForm[];
  db: string;
}

/**
 *  Response of POST /json/admin/init. Several keys keep the snake_case names
 *  they have in the settings table, so they are mapped explicitly when they
 *  reach the store rather than spread in blind.
 */
export interface AdminInitResponse {
  username: string;
  userId: number;
  groupId: number;
  roles: string;
  instance: string;

  responsible_user: boolean;
  contactEmail: string | null;
  superUserGroup: boolean;
  hasSuperUserGroup: boolean;
  hasOrganization: boolean;
  isOrganization: boolean;

  activeUsers: number;
  no_of_office_users: number;
  max_users?: number | string;
  monthlyLicences?: number | string;
  payment_schedule?: string;
  pricing_plan?: string;
  max_password_age?: number | string;
  admin_disable_password?: boolean | string;
  saml_idp_metadata?: string;

  transcriptionUsage: number;
  transcriptionUsageUnits: string;
  transcriptionBudget: number;
  transcriptionBudgetUnits: string;
  aiCredits: number;
  aiUsage: number;

  samlEnabled: boolean;
  accessSharedDataOnlyEnabled: boolean;
}

// ---------------------------------------------------------------- loading

export const fetchAdminInit = () => post<AdminInitResponse>('/json/admin/init');

export const fetchUserGroups = () => post<UserGroupsResponse>('/json/admin/loadUserGroups');

export const fetchUsers = () => post<AdminUser[]>('/json/admin/loadUsers');

export const fetchHierarchies = async (): Promise<AdminLocationHierarchy[]> => {
  const res = await post<{ hierarchyList: AdminLocationHierarchy[] }>('/json/admin/loadHierarchy');
  return res.hierarchyList || [];
};

export const fetchCampaigns = async (): Promise<Campaign[]> => {
  const res = await get<{ campaignList: Campaign[] }>('/json/admin/loadCampaigns');
  return res.campaignList || [];
};

export const fetchLocations = async (hierarchyId: number): Promise<LocationNode[]> => {
  const res = await post<{ locationNodes: LocationNode[] }>(`/json/admin/loadLocations/${hierarchyId}`);
  return res.locationNodes || [];
};

export const fetchGroup = async (id: number): Promise<AdminGroup | undefined> => {
  const res = await get<{ group?: AdminGroup }>(`/json/admin/group/${id}`);
  return res.group;
};

export const fetchUserStats = () => get<UserStats>('/json/admin/getUserStats');

// ---------------------------------------------------------------- saving

/** Outcome of a group save, so callers can react without re-reading state. */
export interface AdminGroupSaveResult {
  saved: boolean;
  group?: AdminGroup;
}

export const saveGroup = (group: AdminGroupDraft) =>
  post<AdminResponse>('/json/admin/save/group', group);

export const saveUser = (user: AdminUserDraft) =>
  post<AdminResponse>('/json/admin/save/user', user);

export const saveCampaign = (campaign: CampaignDraft) =>
  post<AdminResponse>('/json/admin/save/campaign', campaign);

// ---------------------------------------------------------------- deleting

export const deleteUsers = (ids: number[]) =>
  post<AdminResponse>('/json/admin/delete/user', { ids });

export type GroupDeleteMode = 'mark-deleted' | 'permanent-delete';

export const deleteGroups = (ids: number[], deleteData: GroupDeleteMode) =>
  post<AdminResponse>('/json/admin/delete/group', { ids, deleteData });

// ---------------------------------------------------------------- reporting

export const fetchAccessLogs = async (
  type: GridMode,
  ids: number[],
  startDate = '',
  endDate = '',
): Promise<AccessLogItem[]> => {
  const query = `startDate=${startDate}&endDate=${endDate}&ids=${ids.join(',')}`;
  const res = await get<{ accessLogItems: AccessLogItem[] }>(`/json/admin/accessLogs/${type}?${query}`);
  return res.accessLogItems || [];
};

export interface ActivityQuery {
  users?: number[];
  groups?: number[];
  period: ActivityPeriod;
  /** 1 to include subform submissions in the counts. */
  subform?: 0 | 1;
  /** 1 to count each data point once rather than per submission. */
  countUnique?: 0 | 1;
}

export const fetchActivity = ({
  users = [], groups = [], period, subform = 0, countUnique = 0,
}: ActivityQuery): Promise<ActivityItem[]> => {
  const query = `users=${users.join(',')}&period=${period}&groups=${groups.join(',')}` +
    `&subform=${subform}&countUnique=${countUnique}`;
  return get<ActivityItem[]>(`/json/admin/activity?${query}`);
};

/** Opens the export in a new tab; the endpoint streams a file, not JSON. */
export const exportUsersUrl = (ids: number[], userName: string, key: string): string =>
  `/json/admin/users/export?userid=${encodeURIComponent(userName)}` +
  `&key=${encodeURIComponent(key)}&users=${ids.join(',')}`;

// ---------------------------------------------------------------- settings

export const fetchInviteTemplate = () => get<InviteTemplate>('/json/admin/get/template');

export interface TemplateSetting {
  name: string;
  value: string;
}

export const saveInviteTemplate = (settings: TemplateSetting[]) =>
  post<AdminResponse>('/json/admin/save/template', settings);

export const sendEmail = (to: string, subject: string, message: string) =>
  post<AdminResponse>('/json/admin/send/email', { to, subject, message });

// ---------------------------------------------------------------- html zip

export type HtmlZipType = 'group' | 'survey';

export const removeHtmlZip = (type: HtmlZipType, id: number) =>
  post<AdminResponse>(`/json/admin/removeHtmlZip/${type}/${id}`);

export const openHtmlZipUrl = (type: HtmlZipType, id: number): string =>
  `/json/admin/openHtmlZip/${type}/${id}`;

/**
 *  Creates a form list out of the forms picked on a group. GroupSync did this
 *  after every save where forms were selected but no form list was chosen —
 *  without it the group silently keeps no forms.
 */
export const addFormsToFormList = (
  identifiers: string[], identifier: string, name: string, groupId: number,
) => post<AdminResponse>('/json/atool/app/addFormToFormList', { identifiers, identifier, name, groupId });
